import { useState, useRef, useCallback } from 'react';
import ImageModal from './ImageModal';
import { useVariant } from '../context/VariantContext';
import type { PlanContent } from '../data/types';

function AccordionItem({ title, children }: { title: string; children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);

  return (
    <div className="lp__accordion-item">
      <div className="lp__accordion-item__header" onClick={toggle}>
        <span className="lp__accordion-item__icon">Q</span>
        <span className="lp__accordion-item__title">{title}</span>
        <span className="lp__accordion-item__toggle">{isOpen ? '−' : '+'}</span>
      </div>
      <div
        ref={contentRef}
        className="lp__accordion-item__content"
        style={{ maxHeight: isOpen ? `${contentRef.current?.scrollHeight}px` : '0' }}
      >
        {children}
      </div>
    </div>
  );
}

function PlanCard({ plan }: { plan: PlanContent }) {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="lp__plan-card">
      <div className="lp__plan-card__badge">{plan.badge}</div>
      <div className="lp__plan-card__inner">
        <div className="lp__plan-card__price-row">
          <div className="lp__plan-card__price-group">
            <span className="lp__plan-card__price-label">{plan.initialPrice.label}</span>
            <span className={`lp__plan-card__price-value${plan.initialPrice.large ? ' lp__plan-card__price-value--large' : ''}${plan.initialPrice.accent ? ' lp__plan-card__price-value--accent' : ''}`}>
              {plan.initialPrice.value}
            </span>
            <span className="lp__plan-card__price-unit">
              {plan.initialPrice.tax && <span className="lp__plan-card__price-tax">（税込）</span>}
              <span className="lp__plan-card__price-yen">{plan.initialPrice.unit}</span>
            </span>
            {plan.initialPrice.note && (
              <div className="lp__plan-card__price-note">{plan.initialPrice.note}</div>
            )}
          </div>
          <span className="lp__plan-card__plus">＋</span>
          <div className="lp__plan-card__price-group">
            {plan.monthlyPrice.note && (
              <div className="lp__plan-card__note">{plan.monthlyPrice.note}</div>
            )}
            <span className="lp__plan-card__price-label">{plan.monthlyPrice.label}</span>
            <span className={`lp__plan-card__price-value${plan.monthlyPrice.large ? ' lp__plan-card__price-value--large' : ''}${plan.monthlyPrice.accent ? ' lp__plan-card__price-value--accent' : ''}`}>
              {plan.monthlyPrice.value}
            </span>
            <span className="lp__plan-card__price-unit">
              {plan.monthlyPrice.tax && <span className="lp__plan-card__price-tax">（税込）</span>}
              <span className="lp__plan-card__price-yen">{plan.monthlyPrice.unit}</span>
            </span>
          </div>
        </div>

        <div className="lp__plan-card__features">
          <h4 className="lp__plan-card__features-title">{plan.features.title}</h4>
          <div className="lp__plan-card__features-divider" />
          {plan.features.items.map((f) => (
            <p className="lp__plan-card__feature-item" key={f}>{f}</p>
          ))}
        </div>

        {plan.appPromo && (
          <>
            <div className="lp__plan-card__app-promo">
              <div className="lp__plan-card__app-promo-image" onClick={() => setModalOpen(true)}>
                <img src={plan.appPromo.images[0].src} alt={plan.appPromo.images[0].alt} loading="lazy" />
                <span className="lp__plan-card__app-promo-zoom">タップで拡大</span>
              </div>
              <div className="lp__plan-card__app-promo-body">
                <h4 className="lp__plan-card__app-promo-title">{plan.appPromo.title}</h4>
                <p className="lp__plan-card__app-promo-desc">{plan.appPromo.description}</p>
              </div>
            </div>
            <ImageModal
              images={plan.appPromo.images}
              isOpen={modalOpen}
              onClose={() => setModalOpen(false)}
            />
          </>
        )}

        {plan.accordion && (
          <AccordionItem title={plan.accordion.title}>
            <p className="lp__accordion-answer">{plan.accordion.answer}</p>
            <p className="lp__accordion-note">{plan.accordion.note}</p>
          </AccordionItem>
        )}
      </div>
    </div>
  );
}

export default function Pricing() {
  const { pricing } = useVariant();

  return (
    <section className="lp__pricing" id="pricing">
      {/* 上半分の白背景 */}
      <div className="lp__pricing-bg-top" />

      {/* セクション画像 */}
      <div className="lp__pricing-hero">
        <img src={pricing.heroImage} alt="" loading="lazy" />
      </div>

      {/* プランカード */}
      {pricing.plans.map((plan) => (
        <PlanCard key={plan.badge} plan={plan} />
      ))}

      {/* 補助金案内 */}
      {pricing.subsidyNote && (
        <div className="lp__pricing-subsidy">
          <p className="lp__pricing-subsidy__text">{pricing.subsidyNote}</p>
        </div>
      )}

      {/* プランFAQ */}
      {pricing.pricingFaqs && pricing.pricingFaqs.length > 0 && (
        <div className="lp__pricing-faqs">
          {pricing.pricingFaqs.map((faq) => (
            <AccordionItem key={faq.question} title={faq.question}>
              <p className="lp__accordion-answer">{faq.answer}</p>
            </AccordionItem>
          ))}
        </div>
      )}

      {/* パッケージ写真 */}
      <div className="lp__package-photo">
        <img src={pricing.packageImage} alt="パッケージ内容イメージ" loading="lazy" />
        <p className="lp__image-note">※写真はイメージです</p>
      </div>

      {/* パッケージ本体 */}
      <div className="lp__package-info">
        <h4 className="lp__package-info__title">パッケージ本体</h4>
        <div className="lp__package-info__divider" />
        {pricing.packageItems.map((item) => (
          <p className="lp__package-info__item" key={item}>{item}</p>
        ))}
      </div>

      {/* 商品詳細 */}
      <div className="lp__product-detail">
        <h4 className="lp__product-detail__title">商品詳細</h4>
        <div className="lp__product-detail__divider" />
        <p className="lp__product-detail__subtitle">{pricing.productSubtitle}</p>
        <div className="lp__product-detail__list">
          {pricing.productDetails.map((d) => (
            <div className="lp__product-detail__row" key={d.item}>
              <p className="lp__product-detail__item">{d.item}</p>
              <p className="lp__product-detail__value">{d.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 注意事項 */}
      <div className="lp__pricing-notice">
        <p className="lp__pricing-notice__title">※注意事項</p>
        <ul className="lp__pricing-notice__list">
          {pricing.notices.map((notice, i) => (
            <li key={i}>{notice}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}
